import { useRef, useCallback, useState } from 'react';

interface AudioMetrics {
  volume: number;
  noiseLevel: number;
  isSilent: boolean;
  isClipping: boolean;
}

export const useAudioAnalyzer = () => {
  const [metrics, setMetrics] = useState<AudioMetrics>({
    volume: 0,
    noiseLevel: 0,
    isSilent: true,
    isClipping: false
  });
  
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyzerRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const startAnalyzing = useCallback((stream: MediaStream) => {
    audioContextRef.current = new AudioContext();
    analyzerRef.current = audioContextRef.current.createAnalyser();
    
    const source = audioContextRef.current.createMediaStreamSource(stream);
    source.connect(analyzerRef.current);
    
    analyzerRef.current.fftSize = 2048;
    const bufferLength = analyzerRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const analyze = () => {
      if (!analyzerRef.current) return;
      
      analyzerRef.current.getByteFrequencyData(dataArray);
      
      // Calculate average volume
      const sum = dataArray.reduce((a, b) => a + b, 0);
      const average = sum / bufferLength;
      const volume = Math.min(100, (average / 128) * 100);
      
      // Detect silence (volume below threshold for extended period)
      const isSilent = volume < 5;
      
      // Detect clipping (too loud)
      const maxValue = Math.max(...Array.from(dataArray));
      const isClipping = maxValue > 250;
      
      // Calculate noise level (high frequency content)
      const highFreqStart = Math.floor(bufferLength * 0.7);
      const highFreqSum = dataArray.slice(highFreqStart).reduce((a, b) => a + b, 0);
      const noiseLevel = (highFreqSum / (bufferLength - highFreqStart)) / 255 * 100;
      
      setMetrics({
        volume,
        noiseLevel,
        isSilent,
        isClipping
      });
      
      animationFrameRef.current = requestAnimationFrame(analyze);
    };
    
    analyze();
  }, []);

  const stopAnalyzing = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    setMetrics({
      volume: 0,
      noiseLevel: 0,
      isSilent: true,
      isClipping: false
    });
  }, []);

  return {
    metrics,
    startAnalyzing,
    stopAnalyzing
  };
};